// groupReportLink — the logic behind the Reports show page's "Reported
// group" panel (Task 8, Groups Phase 2 admin), split out of show.tsx so it's
// testable without mounting the page's full Refine (useShow/useUpdate/
// useGetIdentity) harness — this repo has no existing precedent for testing
// THAT, and the actual new behavior worth pinning is this resolution rule
// and RPC call shape, not React wiring. Follows the module's own
// `vi.mock('../../lib/supabaseClient', …)` convention (see
// admin/src/__tests__/feedBulkPurge.test.ts) rather than dependency
// injection, so show.tsx imports and calls these exactly like any other
// supabaseClient caller in this codebase (GroupMembersTable.tsx, etc.).
import { supabaseClient } from '../../lib/supabaseClient'

export type GroupReportTargetType = 'group' | 'group_meetup'

/** True for the two target_types this panel handles — group and group_meetup. */
export function isGroupReportTarget(targetType: string): targetType is GroupReportTargetType {
  return targetType === 'group' || targetType === 'group_meetup'
}

/** The admin route for a group's Show page (App.tsx: name: 'groups', show: '/groups/show/:id'). */
export function groupShowPath(groupId: string): string {
  return `/groups/show/${groupId}`
}

/**
 * Resolve the group id a report actually points at. A 'group' report names
 * the group directly (target_id IS the group id — no query needed). A
 * 'group_meetup' report names a MEETUP; group_meetups isn't a registered
 * Refine resource (no admin CRUD page of its own yet), so this is one direct
 * `group_meetups.group_id` lookup — the same ad hoc-query pattern
 * GroupMembersTable.tsx already uses for cross-table reads the data
 * provider's resource registry doesn't cover. Returns null for every other
 * target_type, and for a meetup that no longer exists (its report can still
 * be reviewed; there is just no group left to link to or suspend).
 */
export async function resolveReportGroupId(report: {
  target_type: string
  target_id: string
}): Promise<string | null> {
  if (report.target_type === 'group') return report.target_id
  if (report.target_type !== 'group_meetup') return null
  const { data, error } = await supabaseClient
    .from('group_meetups')
    .select('group_id')
    .eq('id', report.target_id)
    .maybeSingle()
  if (error || !data) return null
  return (data.group_id as string | null) ?? null
}

/**
 * Suspend the group behind a report. Thin wrapper over the SAME
 * `set_group_status` RPC the Group Show page's own Suspend button calls
 * (groups/show.tsx) — one status-change entry point, reached from two admin
 * screens.
 */
export async function suspendReportedGroup(groupId: string): Promise<void> {
  const { error } = await supabaseClient.rpc('set_group_status', { p_group: groupId, p_status: 'suspended' })
  if (error) throw error
}
